import { useEffect, useState } from 'react';
import { API_BASE } from '../../../config/api';

interface GenreFrequency {
    genre: string;
    album_count: number;
}

// How many genres show before the "... more" expander.
const PREVIEW_COUNT = 5;

// Derived genre list for an artist - aggregated from their albums' genres
// (fetched from Deezer during album identify), most frequent first. Shows
// the top PREVIEW_COUNT as a comma-joined line with a clickable "... more"
// that expands the full list; "less" collapses it back. Renders nothing
// while loading or when there's no genre data at all.
export function ArtistGenres({ artistId }: { artistId: number }): React.JSX.Element | null {
    const [genres, setGenres] = useState<GenreFrequency[] | null>(null);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        setGenres(null);
        setExpanded(false);
        fetch(`${API_BASE}/api/artist?id=${artistId}&view=genres`, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : []))
            .then((g) => setGenres(Array.isArray(g) ? g : []))
            .catch(() => setGenres([]));
    }, [artistId]);

    if (!genres || genres.length === 0) return null;

    const shown = expanded ? genres : genres.slice(0, PREVIEW_COUNT);
    const hasMore = genres.length > PREVIEW_COUNT;

    return (
        <span className="detailHeroMuted detailGenres">
            {shown.map((g) => g.genre).join(', ')}
            {hasMore && !expanded && (
                <button
                    type="button"
                    className="detailGenresMore"
                    onClick={() => setExpanded(true)}
                    aria-label={`Show all ${genres.length} genres`}
                >
                    {' '}… more
                </button>
            )}
            {expanded && hasMore && (
                <button
                    type="button"
                    className="detailGenresMore"
                    onClick={() => setExpanded(false)}
                    aria-label="Show fewer genres"
                >
                    {' '}… less
                </button>
            )}
        </span>
    );
}
